import React, { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { Container } from '@mui/system';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { CONFIG } from '~/config';
import { ExternalRestClient } from '~/modules/requests';
import { setWizardStep, startWizard } from '~/store/slices/itinerary-wizard';
import { ItineraryPackage } from '~/types';
import { createItineraryIdempotencyKey } from '~/utils/itinerary-checkout';

const ItineraryBuyRoute: React.FC = () => {
  const { packageId } = useParams<{ packageId: string }>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let isMounted = true;

    const startOrderFlow = async () => {
      if (!packageId) {
        setErrorMessage('Package ID is required to start an itinerary request.');
        return;
      }

      try {
        const response = await ExternalRestClient.get<ItineraryPackage>(
          `${CONFIG.env.apiBaseUrl}v2/packages/itinerary/${packageId}`,
        );

        if (!isMounted) return;

        if (response.status !== 200 || !response.data?.id) {
          setErrorMessage('We could not find the selected itinerary package. Please choose another package.');
          return;
        }

        const idempotencyKey = createItineraryIdempotencyKey();
        dispatch(startWizard({ idempotencyKey, packageId: response.data.id }));
        dispatch(setWizardStep(2));
        navigate(`/itinerary/${idempotencyKey}`, { replace: true });
      } catch (error: any) {
        if (!isMounted) return;

        setErrorMessage(
          error?.response?.data?.error || 'We could not start your itinerary request. Please try again later.',
        );
      }
    };

    startOrderFlow();

    return () => {
      isMounted = false;
    };
  }, [dispatch, navigate, packageId]);

  return (
    <Box sx={{ bgcolor: '#fff', minHeight: '60vh', py: 8 }}>
      <Container>
        {errorMessage ? (
          <Alert severity="error">{errorMessage}</Alert>
        ) : (
          <Box sx={{ alignItems: 'center', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <CircularProgress />
            <Typography variant="h6">Starting your itinerary request...</Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default ItineraryBuyRoute;